import { ReactNode } from "react";
import {
    Box,
    Container,
    Typography,
    Paper,
    Alert,
    CircularProgress
} from "@mui/material";
import { useTranslation } from "react-i18next";

interface ListLayoutProps {
    title: ReactNode;
    subtitle?: string;
    filters?: ReactNode;
    actions?: ReactNode;
    error?: unknown;
    isLoading?: boolean;
    isEmpty?: boolean;
    emptyMessage?: string;
    children: ReactNode;
}

export default function ListLayout({
    title,
    subtitle,
    filters,
    actions,
    error,
    isLoading,
    isEmpty,
    emptyMessage,
    children
}: ListLayoutProps) {
    const { t } = useTranslation();
    const resolvedEmptyMessage = emptyMessage || t("common.noRecords");
    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box sx={{
                mb: 4,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                gap: 2
            }}>
                <Box>
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        gutterBottom
                        sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="subtitle1" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {actions && (
                    <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                        {actions}
                    </Box>
                )}
            </Box>

            {filters && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    {filters}
                </Paper>
            )}

            {error ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {t("common.errorLoadingData")}
                </Alert>
            ) : null}

            <Box sx={{ position: "relative", minHeight: 200 }}>
                {isLoading && isEmpty ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {isEmpty ? (
                            <>
                                <Alert severity="info">{resolvedEmptyMessage}</Alert>
                                <Box sx={{ display: "none" }}>{children}</Box>
                            </>
                        ) : (
                            children
                        )}

                        {isLoading && !isEmpty && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    bgcolor: "rgba(255, 255, 255, 0.5)",
                                    zIndex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Container>
    );
}
